/**
 * Iframe ↔ parent communication bridge.
 *
 * When the softphone is embedded via <iframe> (Mode B/C), it can send events
 * to the host page (e.g. opened call → host opens CRM popup) and accept
 * commands (e.g. host tells us to dial a number on a click).
 */

export type OutboundEventType =
  | 'ready'
  | 'registered'
  | 'incoming_call'
  | 'call_answered'
  | 'call_ended'
  | 'unregistered';

export type InboundCommand =
  | { command: 'dial'; number: string }
  | { command: 'hangup' }
  | { command: 'mute'; mute: boolean }
  | { command: 'hold'; hold: boolean };

export interface BridgeOptions {
  /** Whitelist of origins that may send commands. '*' allows all (dev only). */
  allowedOrigins?: string[] | '*';
  onCommand?: (cmd: InboundCommand) => void;
}

export class PostMessageBridge {
  private removeListener?: () => void;

  constructor(private readonly opts: BridgeOptions = {}) {}

  start() {
    if (typeof window === 'undefined' || window.parent === window) return;
    const handler = (e: MessageEvent) => {
      const allowed = this.opts.allowedOrigins;
      if (allowed && allowed !== '*' && !allowed.includes(e.origin)) return;
      const data = e.data;
      if (!data || typeof data !== 'object' || typeof data.command !== 'string') return;
      this.opts.onCommand?.(data as InboundCommand);
    };
    window.addEventListener('message', handler);
    this.removeListener = () => window.removeEventListener('message', handler);
    this.send('ready', {});
  }

  stop() {
    this.removeListener?.();
  }

  send(type: OutboundEventType, payload: Record<string, unknown>) {
    if (typeof window === 'undefined' || window.parent === window) return;
    try {
      window.parent.postMessage({ source: 'ccark', type, ...payload }, '*');
    } catch { /* */ }
  }
}

import { isDesktop, launchBrowser } from './desktop';

/**
 * Open a CRM popup URL by rendering a template against call data.
 * Variables: {{caller}} {{callee}} {{ext}} {{call_id}} {{direction}}
 *
 * Desktop (Tauri) mode honours `desktopBrowser` to force a specific
 * browser binary (chrome/firefox/edge/default). Web fallback uses
 * `window.open` with the chosen openMode.
 */
export function openCrmPopup(
  template: string,
  openMode: 'new_tab' | 'popup' | 'named' | 'embedded',
  vars: { caller?: string; callee?: string; ext?: string; call_id?: string; direction?: string },
  bridge?: PostMessageBridge,
  desktopBrowser: 'chrome' | 'firefox' | 'edge' | 'default' = 'default',
) {
  const caller = vars.caller ?? '';
  const callee = vars.callee ?? '';
  const ext = vars.ext ?? '';
  const callId = vars.call_id ?? '';
  const direction = vars.direction ?? '';

  // Support both Mustache-style ({{caller}}) and 3CX/Arkbase-style (%CallerNumber%) placeholders.
  const url = template
    // Mustache
    .replaceAll('{{caller}}', caller)
    .replaceAll('{{callee}}', callee)
    .replaceAll('{{ext}}', ext)
    .replaceAll('{{call_id}}', callId)
    .replaceAll('{{direction}}', direction)
    // 3CX-style
    .replaceAll('%CallerNumber%', caller)
    .replaceAll('%CalledNumber%', callee)
    .replaceAll('%Extension%', ext)
    .replaceAll('%CallID%', callId)
    .replaceAll('%Direction%', direction);

  console.log('[crm-popup] opening', { url, openMode, isDesktop, desktopBrowser });

  // Try Tauri first — works even if isDesktop detection failed, as long as the
  // @tauri-apps/api invoke can reach the Rust handler. Fall back to window.open
  // if the invoke throws (= we're in a plain browser).
  launchBrowser(url, desktopBrowser)
    .then(() => console.log('[crm-popup] launched via Tauri'))
    .catch((e) => {
      console.log('[crm-popup] Tauri launch unavailable, falling back to window.open', e?.message);
      openInBrowser(url, openMode, vars, bridge);
    });
}

function openInBrowser(
  url: string,
  openMode: 'new_tab' | 'popup' | 'named' | 'embedded',
  vars: { ext?: string },
  bridge?: PostMessageBridge,
) {
  let win: Window | null = null;
  switch (openMode) {
    case 'new_tab':
      win = window.open(url, '_blank');
      break;
    case 'popup':
      win = window.open(url, 'ccark_crm', 'width=900,height=700,left=200,top=100');
      break;
    case 'named':
      win = window.open(url, `ccark_crm_${vars.ext ?? 'default'}`);
      break;
    case 'embedded':
      bridge?.send('incoming_call', { crm_url: url });
      return;
  }
  if (!win) {
    console.warn('[crm-popup] window.open returned null — popup blocked by browser?');
  }
}
